
import _ from 'underscore';
import { lazy } from 'react';
import { DefaultHeaderDropdown } from '@bingads-webui-campaign-react/overview-card';

import {
  cardWelcomeTipsId,
  cardPerformanceId,
  cardRecommendationsId,
  cardTopCampaignsId,
  cardTopAccountsId,
  cardTopKeywordsId,
  cardDeviceId,
  cardSearchTermsId,
  cardCompetitionId,
  cardTopAdGroupsId,
  cardConversionTrackingId,
  cardTopLocationsId,
  cardTopChangesAccountId,
  cardTopChangesCampaignId,
  cardTopChangesAdGroupId,
  cardDiagnosticId,
  cardAssetPerformanceId,
  cardNavigateToFullProductId,
} from '@bingads-webui-campaign/fluent-page-consts';
import {
  getHeaderPropsFromPreferenceForPerformanceTable,
  getTablePropsFromPreferenceForPerformanceTable,
  getPropsFromPreferenceForPerformanceChart,
  getPropsFromPreferenceForTopLocations,
  getTablePropsFromPreferenceForDevices,
  getPropsFromPreferenceForConversionTracking,
  getHeaderPropsFromPreferenceForConversionTracking,
  getPropsForRecommendations,
  getPropsForTopChanges,
  getHeaderPropsFromPreferenceForTopChanges,
} from '@bingads-webui-campaign/fluent-page-data';

const PerformanceCardContainer = lazy(() => import(/* webpackMode: "eager" */'./performance-chart-card/container').then(module => ({ default: module.PerformanceCardContainer })));
const OverviewRecommendationsCard = lazy(() => import(/* webpackMode: "eager" */'@bingads-webui-campaign-react/overview-recommendations-card').then(module => ({ default: module.OverviewRecommendationsCard })));
const OverviewCampaignTable = lazy(() => import(/* webpackMode: "eager" */'@bingads-webui-campaign-react/overview-grid-card-fluent').then(module => ({ default: module.OverviewCampaignTable })));
const OverviewAccountTable = lazy(() => import(/* webpackMode: "eager" */'@bingads-webui-campaign-react/overview-grid-card-fluent').then(module => ({ default: module.OverviewAccountTable })));
const OverviewKeywordTable = lazy(() => import(/* webpackMode: "eager" */'@bingads-webui-campaign-react/overview-grid-card-fluent').then(module => ({ default: module.OverviewKeywordTable })));
const OverviewSearchTermTable = lazy(() => import(/* webpackMode: "eager" */'@bingads-webui-campaign-react/overview-grid-card-fluent').then(module => ({ default: module.OverviewSearchTermTable })));
const OverviewAdGroupTable = lazy(() => import(/* webpackMode: "eager" */'@bingads-webui-campaign-react/overview-grid-card-fluent').then(module => ({ default: module.OverviewAdGroupTable })));
const ConversionTrackingCardFluent = lazy(() => import(/* webpackMode: "eager" */'@bingads-webui-campaign/overview-conversion-tracking-card').then(module => ({ default: module.ConversionTrackingCardFluent })));
const DeviceCard = lazy(() => import(/* webpackMode: "eager" */'@bingads-webui-campaign/overview-device-card-fluent').then(module => ({ default: module.DeviceCard })));
const TopLocationsCard = lazy(() => import(/* webpackMode: "eager" */'@bingads-webui-campaign-react/top-locations-card').then(module => ({ default: module.TopLocationsCard })));
const TopLocationViewSelector = lazy(() => import(/* webpackMode: "eager" */'@bingads-webui-campaign-react/top-locations-card').then(module => ({ default: module.TopLocationViewSelector })));
const CompetitionCardWithData = lazy(() => import(/* webpackMode: "eager" */'@bingads-webui-campaign-react/overview-competition-card').then(module => ({ default: module.CompetitionCardWithData })));
const CompetitionFilterWithData = lazy(() => import(/* webpackMode: "eager" */'@bingads-webui-campaign-react/overview-competition-card').then(module => ({ default: module.CompetitionFilterWithData })));
const TopChangesCard = lazy(() => import(/* webpackMode: "eager" */'@bingads-webui-campaign/overview-top-changes-card-fluent').then(module => ({ default: module.TopChangesCard })));
const WelcomeTipsCard = lazy(() => import(/* webpackMode: "eager" */'@bingads-webui-campaign/overview-welcome-tips-card').then(module => ({ default: module.WelcomeTipsCard })));
const AssetPerformanceCard = lazy(() => import(/* webpackMode: "eager" */'@bingads-webui-campaign-react/asset-performance-card').then(module => ({ default: module.AssetPerformanceCardWithAssetData })));
const AssetPerformanceCardHeader = lazy(() => import(/* webpackMode: "eager" */'@bingads-webui-campaign-react/asset-performance-card').then(module => ({ default: module.AssetPerformanceFilterWithAssetGroupsData })));
const DiagnosticCard = lazy(() => import(/* webpackMode: "eager" */'@bingads-webui-campaign/overview-diagnostic-card').then(module => ({ default: module.OverviewDiagnosticCard })));
const NavigateToFullProductCard = lazy(() => import(/* webpackMode: "eager" */'@bingads-webui-campaign-react/sdk-campaign-overview-page').then(module => ({ default: module.NavigateToFullProductCard })));

export const getCardViewConstructor = ({
  config,
  instrumentation,
  constants,
  campaignType,
}) => {
  switch (config.typeId) {
    case cardPerformanceId:
      return {
        CardView: PerformanceCardContainer,
        getCardViewProps: ({ cardPreference, patchPreference, permissions }) => getPropsFromPreferenceForPerformanceChart({
          cardPreference, patchPreference, permissions, campaignType,
        }),
        config,
      };
    case cardRecommendationsId:
      return {
        CardView: OverviewRecommendationsCard,
        getCardViewProps: getPropsForRecommendations,
        config,
      };
    case cardTopCampaignsId:
      return {
        CardView: OverviewCampaignTable,
        getCardViewProps: ({
          cardPreference, patchPreference, permissions, navigationLevel, i18n,
        }) =>
          getTablePropsFromPreferenceForPerformanceTable({
            cardPreference, patchPreference, permissions, navigationLevel, i18n, campaignType,
          }),
        CardHeaderContent: DefaultHeaderDropdown,
        getCardHeaderContentProps: getHeaderPropsFromPreferenceForPerformanceTable,
        config,
      };
    case cardTopKeywordsId:
      return {
        CardView: OverviewKeywordTable,
        getCardViewProps: getTablePropsFromPreferenceForPerformanceTable,
        CardHeaderContent: DefaultHeaderDropdown,
        getCardHeaderContentProps: getHeaderPropsFromPreferenceForPerformanceTable,
        config,
      };
    case cardSearchTermsId:
      return {
        CardView: OverviewSearchTermTable,
        getCardViewProps: getTablePropsFromPreferenceForPerformanceTable,
        config,
      };
    case cardTopAdGroupsId:
      return {
        CardView: OverviewAdGroupTable,
        getCardViewProps: getTablePropsFromPreferenceForPerformanceTable,
        CardHeaderContent: DefaultHeaderDropdown,
        getCardHeaderContentProps: getHeaderPropsFromPreferenceForPerformanceTable,
        config,
      };
    case cardConversionTrackingId:
      return {
        CardView: ConversionTrackingCardFluent,
        CardHeaderContent: DefaultHeaderDropdown,
        getCardViewProps: getPropsFromPreferenceForConversionTracking,
        getCardHeaderContentProps: getHeaderPropsFromPreferenceForConversionTracking,
        config,
      };
    case cardDeviceId:
      return {
        CardView: DeviceCard,
        getCardViewProps: getTablePropsFromPreferenceForDevices,
        config,
      };
    case cardTopLocationsId:
      return {
        CardView: TopLocationsCard,
        getCardViewProps: getPropsFromPreferenceForTopLocations,
        CardHeaderContent: TopLocationViewSelector,
        config,
      };
    case cardCompetitionId:
      return {
        CardView: CompetitionCardWithData,
        CardHeaderContent: CompetitionFilterWithData,
        config,
      };
    case cardTopChangesCampaignId:
    case cardTopChangesAccountId:
    case cardTopChangesAdGroupId:
      return {
        CardView: TopChangesCard,
        getCardViewProps: getPropsForTopChanges,
        CardHeaderContent: DefaultHeaderDropdown,
        getCardHeaderContentProps: getHeaderPropsFromPreferenceForTopChanges,
        config,
      };
    case cardWelcomeTipsId:
      return {
        CardView: WelcomeTipsCard,
        config,
      };
    case cardTopAccountsId:
      return {
        CardView: OverviewAccountTable,
        getCardViewProps: ({
          cardPreference, patchPreference, permissions, navigationLevel, i18n,
        }) =>
          getTablePropsFromPreferenceForPerformanceTable({
            cardPreference, patchPreference, permissions, navigationLevel, i18n,
          }),
        CardHeaderContent: DefaultHeaderDropdown,
        getCardHeaderContentProps: getHeaderPropsFromPreferenceForPerformanceTable,
        config,
      };
    case cardDiagnosticId:
      return {
        CardView: DiagnosticCard,
        getCardViewProps: ({ campaignType }) => ({ campaignType }),
        config,
      };
    case cardAssetPerformanceId:
      return {
        CardView: AssetPerformanceCard,
        CardHeaderContent: AssetPerformanceCardHeader,
        getCardViewProps: ({ campaignType }) => ({ campaignType }),
        getCardHeaderContentProps: ({ isSDK }) => ({ isSDK }),
        config,
      };
    case cardNavigateToFullProductId:
      return {
        CardView: NavigateToFullProductCard,
        config,
      };
    default:
      instrumentation.logMessage([constants.LOGCATEGORY.ERROR], constants.LOGLEVEL.INFO, 'dashboard/dashboard-view', `Unknown card content: ${config.contentType}`);
      return null;
  }
};
